// Mirrors backend/constants/lab.js — these strings travel as query params and
// request bodies, so the two files must stay in step.

export const LAB_ORDER_STATUS = {
  CREATED: "created",
  SCHEDULED: "scheduled",
  RESCHEDULED: "rescheduled",
  KIT_DISPATCHED: "kit_dispatched",
  PHLEBO_ASSIGNED: "phlebo_assigned",
  PHLEBO_STARTED: "phlebo_started",
  PATIENT_VISITED: "patient_visited",
  SAMPLE_COLLECTED: "sample_collected",
  SAMPLE_DELIVERED: "sample_delivered",
  SAMPLE_PROCESSED: "sample_processed",
  PARTIAL_DELIVERED: "partial_delivered",
  REPORT_DELIVERED: "report_delivered",
  PATIENT_MISSED: "patient_missed",
  PHLEBO_NO_SHOW: "phlebo_no_show",
  CANCELLED: "cancelled",
};

export const LAB_ORDER_STATUS_LABEL = {
  [LAB_ORDER_STATUS.CREATED]: "Created",
  [LAB_ORDER_STATUS.SCHEDULED]: "Scheduled",
  [LAB_ORDER_STATUS.RESCHEDULED]: "Rescheduled",
  [LAB_ORDER_STATUS.KIT_DISPATCHED]: "Kit Dispatched",
  [LAB_ORDER_STATUS.PHLEBO_ASSIGNED]: "Phlebo Assigned",
  [LAB_ORDER_STATUS.PHLEBO_STARTED]: "Phlebo Started",
  [LAB_ORDER_STATUS.PATIENT_VISITED]: "Patient Visited",
  [LAB_ORDER_STATUS.SAMPLE_COLLECTED]: "Sample Collected",
  [LAB_ORDER_STATUS.SAMPLE_DELIVERED]: "Sample Delivered",
  [LAB_ORDER_STATUS.SAMPLE_PROCESSED]: "Sample Processed",
  [LAB_ORDER_STATUS.PARTIAL_DELIVERED]: "Partially Reported",
  [LAB_ORDER_STATUS.REPORT_DELIVERED]: "Report Delivered",
  [LAB_ORDER_STATUS.PATIENT_MISSED]: "Patient Missed",
  [LAB_ORDER_STATUS.PHLEBO_NO_SHOW]: "Phlebo No Show",
  [LAB_ORDER_STATUS.CANCELLED]: "Cancelled",
};

export const LAB_STAGE = {
  BOOKED: "booked",
  COLLECTION: "collection",
  PROCESSING: "processing",
  REPORTED: "reported",
  ATTENTION: "attention",
  CANCELLED: "cancelled",
};

export const STAGE_STATUSES = {
  [LAB_STAGE.BOOKED]: [
    LAB_ORDER_STATUS.CREATED, LAB_ORDER_STATUS.SCHEDULED, LAB_ORDER_STATUS.RESCHEDULED,
  ],
  [LAB_STAGE.COLLECTION]: [
    LAB_ORDER_STATUS.KIT_DISPATCHED, LAB_ORDER_STATUS.PHLEBO_ASSIGNED,
    LAB_ORDER_STATUS.PHLEBO_STARTED, LAB_ORDER_STATUS.PATIENT_VISITED,
    LAB_ORDER_STATUS.SAMPLE_COLLECTED,
  ],
  [LAB_STAGE.PROCESSING]: [
    LAB_ORDER_STATUS.SAMPLE_DELIVERED, LAB_ORDER_STATUS.SAMPLE_PROCESSED,
  ],
  [LAB_STAGE.REPORTED]: [
    LAB_ORDER_STATUS.PARTIAL_DELIVERED, LAB_ORDER_STATUS.REPORT_DELIVERED,
  ],
  [LAB_STAGE.ATTENTION]: [
    LAB_ORDER_STATUS.PATIENT_MISSED, LAB_ORDER_STATUS.PHLEBO_NO_SHOW,
  ],
  [LAB_STAGE.CANCELLED]: [LAB_ORDER_STATUS.CANCELLED],
};

export const STAGE_LABEL = {
  [LAB_STAGE.BOOKED]: "Booked",
  [LAB_STAGE.COLLECTION]: "Collection",
  [LAB_STAGE.PROCESSING]: "Processing",
  [LAB_STAGE.REPORTED]: "Reported",
  [LAB_STAGE.ATTENTION]: "Needs attention",
  [LAB_STAGE.CANCELLED]: "Cancelled",
};

export const STATUS_STAGE = Object.entries(STAGE_STATUSES).reduce((acc, [stage, statuses]) => {
  statuses.forEach((s) => { acc[s] = stage; });
  return acc;
}, {});

// The four stages that make up the happy path, in order. Drives the pipeline
// strip at the top of the list screen.
export const PIPELINE = [LAB_STAGE.BOOKED, LAB_STAGE.COLLECTION, LAB_STAGE.PROCESSING, LAB_STAGE.REPORTED];

export const STATUS_OPTIONS = Object.values(LAB_ORDER_STATUS).map((value) => ({
  value,
  label: LAB_ORDER_STATUS_LABEL[value],
}));

export const COLLECTION_TYPE = { HOME: "home", CENTER: "center" };

export const COLLECTION_TYPE_LABEL = {
  [COLLECTION_TYPE.HOME]: "Home Collection",
  [COLLECTION_TYPE.CENTER]: "Center Visit",
};

export const COLLECTION_TYPE_OPTIONS = [
  { value: COLLECTION_TYPE.HOME, label: COLLECTION_TYPE_LABEL[COLLECTION_TYPE.HOME] },
  { value: COLLECTION_TYPE.CENTER, label: COLLECTION_TYPE_LABEL[COLLECTION_TYPE.CENTER] },
];

export const SAMPLE_TYPE_LABEL = {
  blood: "Blood",
  blood_serum: "Blood Serum",
  urine: "Urine",
  stool: "Stool",
  swab: "Swab",
  none: "No sample (scan)",
};

// Badge variant per stage. Coral (primary) stays reserved for CTAs, so stages
// use the neutral/teal/amber/red range instead.
export const STAGE_VARIANT = {
  [LAB_STAGE.BOOKED]: "outline",
  [LAB_STAGE.COLLECTION]: "warning",
  [LAB_STAGE.PROCESSING]: "warning",
  [LAB_STAGE.REPORTED]: "secondary",
  [LAB_STAGE.ATTENTION]: "destructive",
  [LAB_STAGE.CANCELLED]: "destructive",
};

export const formatCurrency = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
